import type { UpdateVariantInput, CampaignType } from '~/types/graphql';
import type { Campaign, Variant } from '../types';

import { isVariantProductVariantsDifferent } from './productVariant';
import { CAMPAIGN_TYPE } from '~/constants/campaign';
import { getThemePageIDBySuffix } from '../utils';

export interface IVariantChangeType {
  hasTemplateChange: boolean;
  hasTrafficChange: boolean;
  hasPriceChange: boolean;
  hasUrlChange: boolean;
}

export type PriceAction = 'create' | 'update' | 'delete' | 'none';

/** A destination is created, changed or detached the same way a price record is. */
export type UrlAction = PriceAction;

/** Neither type renders a theme template -- a price test swaps a duplicated product, a URL test redirects. */
const CAMPAIGN_TYPES_WITHOUT_TEMPLATE: CampaignType[] = [CAMPAIGN_TYPE.PRICE, CAMPAIGN_TYPE.URL];

/**
 * Price and URL variants test no theme template. Sending template fields makes the API reject the
 * whole update once the campaign leaves draft ("cannot update template suffix of a variant in a
 * non-draft campaign"), so such a variant only ever updates its traffic rate.
 */
export const buildVariantUpdateInput = (data: {
  variant: Variant;
  campaignType?: CampaignType | null;
}): UpdateVariantInput => {
  const { variant, campaignType } = data;

  if (campaignType && CAMPAIGN_TYPES_WITHOUT_TEMPLATE.includes(campaignType)) {
    return { trafficRate: variant.trafficRate };
  }

  return {
    themePageID: getThemePageIDBySuffix(variant.templateSuffix),
    templateSuffix: variant.templateSuffix,
    templateType: variant.templateType,
    trafficRate: variant.trafficRate,
  };
};

export const detectVariantChanges = (
  variant: Variant,
  storedVariant?: Variant,
): IVariantChangeType => {
  if (!storedVariant) {
    return {
      hasTemplateChange: false,
      hasTrafficChange: false,
      hasPriceChange: false,
      hasUrlChange: false,
    };
  }

  const hasTemplateChange =
    variant.templateSuffix !== storedVariant.templateSuffix ||
    variant.templateType !== storedVariant.templateType ||
    variant.template?.redirectURL !== storedVariant.template?.redirectURL;

  const hasTrafficChange = variant.trafficRate !== storedVariant.trafficRate;

  const hasPriceChange = variant.price?.baseID !== storedVariant.price?.baseID;

  return {
    hasTemplateChange,
    hasTrafficChange,
    hasPriceChange,
    hasUrlChange: hasVariantUrlChanged(variant, storedVariant),
  };
};

/**
 * Whether a page has actually been picked. A draft starts on an empty destination, and the homepage
 * — like any page picked by URL alone — has no record behind it, so its `baseID` stays empty and the
 * stored URL is what says something was picked.
 */
export const hasVariantUrlDestination = (url?: Variant['url']): boolean =>
  !!url?.baseID || !!url?.redirectURL;

/**
 * The URL is compared alongside the resource: the homepage, and any page picked by URL alone, carries
 * an empty baseID, so two different picks can share one.
 */
const hasVariantUrlChanged = (variant: Variant, storedVariant?: Variant): boolean =>
  variant.url?.baseID !== storedVariant?.url?.baseID ||
  variant.url?.redirectURL !== storedVariant?.url?.redirectURL;

export const determinePriceAction = (variant: Variant, storedVariant?: Variant): PriceAction => {
  const hadPrice = !!storedVariant?.price;
  const hasPrice = !!variant.price;

  if (!hadPrice && hasPrice) return 'create';
  if (hadPrice && !hasPrice) return 'delete';
  if (hadPrice && hasPrice && variant.price?.baseID !== storedVariant?.price?.baseID)
    return 'update';
  return 'none';
};

export const determineUrlAction = (variant: Variant, storedVariant?: Variant): UrlAction => {
  const hadUrl = !!storedVariant?.url;
  const hasUrl = !!variant.url;

  if (!hadUrl && hasUrl) return 'create';
  if (hadUrl && !hasUrl) return 'delete';
  if (hadUrl && hasUrl && hasVariantUrlChanged(variant, storedVariant)) return 'update';
  return 'none';
};

export const detectCampaignSettingsChanged = (
  campaign: Campaign,
  storedCampaign?: Campaign,
): boolean => {
  if (!storedCampaign) return false;

  return (
    campaign.priority !== storedCampaign.priority ||
    JSON.stringify(campaign.targetDevices) !== JSON.stringify(storedCampaign.targetDevices) ||
    JSON.stringify(campaign.targetVisitorTypes) !==
      JSON.stringify(storedCampaign.targetVisitorTypes) ||
    JSON.stringify(campaign.targetChannels) !== JSON.stringify(storedCampaign.targetChannels) ||
    JSON.stringify(campaign.conditions?.targetMarkets) !==
      JSON.stringify(storedCampaign.conditions?.targetMarkets) ||
    JSON.stringify(campaign.conditions?.targetPageIds) !==
      JSON.stringify(storedCampaign.conditions?.targetPageIds)
  );
};

export const mergeUpdatedVariants = (
  variants: Campaign['variants'],
  tasks: Array<{ variantId: string }>,
  results: Array<Partial<Variant> | undefined>,
): Variant[] => {
  const updatedVariantMap = new Map<string, Partial<Variant>>();

  tasks.forEach((task, index) => {
    const result = results[index];
    if (result) {
      updatedVariantMap.set(task.variantId, result);
    }
  });

  return variants
    ?.map((variant) => {
      const updated = variant?.id ? updatedVariantMap.get(variant.id) : null;
      return updated ? { ...variant, ...updated } : variant;
    })
    .filter(Boolean) as Variant[];
};

type VariantTask = {
  variantId: string;
  isOrigin?: boolean;
  hasTemplateChange?: boolean;
  promise: Promise<Partial<Variant> | undefined>;
};

type IVariantTaskPromise = Promise<Partial<Variant> | undefined>;

/** Runs a record update alongside whatever the variant itself already has in flight, merging both results. */
const withVariantTask = (
  promise: IVariantTaskPromise | undefined,
  recordPromise: IVariantTaskPromise,
): IVariantTaskPromise => {
  if (!promise) return recordPromise;

  return Promise.all([promise, recordPromise]).then(([fieldResult, recordResult]) => ({
    ...fieldResult,
    ...recordResult,
  }));
};

export const buildVariantTasks = (data: {
  variants: Campaign['variants'];
  storedVariants?: Campaign['variants'];
  updateVariantAndTemplate: (variant: Variant) => Promise<Variant | undefined>;
  updateVariant: (variant: Variant) => Promise<Variant | undefined>;
  updatePrice: (variant: Variant, storedVariant?: Variant) => IVariantTaskPromise;
  updateUrl: (variant: Variant, storedVariant?: Variant) => IVariantTaskPromise;
  isCGTemplate?: boolean;
}): VariantTask[] => {
  const {
    variants,
    storedVariants,
    updateVariantAndTemplate,
    updateVariant,
    updatePrice,
    updateUrl,
    isCGTemplate,
  } = data;
  if (!variants) return [];

  return variants.reduce<VariantTask[]>((acc, variant) => {
    if (!variant?.id) return acc;

    const storedVariant = storedVariants?.find((v) => v?.id === variant.id);
    const { hasTemplateChange, hasTrafficChange, hasPriceChange, hasUrlChange } =
      detectVariantChanges(variant, storedVariant);

    let promise: IVariantTaskPromise | undefined;

    if (hasTemplateChange) {
      promise = updateVariantAndTemplate(variant);
    } else if (hasTrafficChange && !isCGTemplate) {
      promise = updateVariant(variant);
    }

    if (hasPriceChange) {
      promise = withVariantTask(promise, updatePrice(variant, storedVariant));
    }

    if (hasUrlChange) {
      promise = withVariantTask(promise, updateUrl(variant, storedVariant));
    }

    if (!promise) return acc;

    acc.push({
      variantId: variant.id,
      promise,
      isOrigin: variant.isOrigin,
      hasTemplateChange,
    });

    return acc;
  }, []);
};

/**
 * Kept apart from `buildVariantTasks` because it can't run alongside them:
 * `variantProductVariantsBulkSet` looks the Control up server-side by campaign, so a Control product
 * picked in this same save has to land first or the server rejects it with "the control has no
 * product". Call it once those are done.
 */
export const buildProductVariantTasks = (data: {
  variants: Campaign['variants'];
  storedVariants?: Campaign['variants'];
  updateProductVariants: (variant: Variant) => IVariantTaskPromise;
}): VariantTask[] => {
  const { variants, storedVariants, updateProductVariants } = data;
  if (!variants) return [];

  return variants.reduce<VariantTask[]>((acc, variant) => {
    if (!variant?.id) return acc;

    const storedVariant = storedVariants?.find((v) => v?.id === variant.id);
    const isChanged = isVariantProductVariantsDifferent({
      productVariants: variant.productVariants,
      storedProductVariants: storedVariant?.productVariants,
    });
    if (!isChanged) return acc;

    acc.push({
      variantId: variant.id,
      promise: updateProductVariants(variant),
      isOrigin: variant.isOrigin,
    });

    return acc;
  }, []);
};
